import { Injectable, Injector, inject } from "@angular/core";
import { Observable, catchError, firstValueFrom, map, of, switchMap, tap } from "rxjs";

import * as Forge from "node-forge";

import { DecouplingUserServiceMSO } from "@providers/DecouplingUserServiceMSO.service";

import { toObservable } from "@angular/core/rxjs-interop";
import { ValidationErrors } from "@angular/forms";
import { C_Features } from "@models/C_Features";
import { DataTables } from "@models/I_CheckDataAvailabilityDecoupling";
import { CookieConsentStatus } from "@models/I_CookieConsentStatus";
import { PromotionDecoupling } from "@models/I_DecouplingPromotions";
import { I_DecouplingTrackersCodereID } from "@models/I_DecouplingTrackersCodereID";
import { DecouplingUserData, RequestUpdateData, UserData } from "@models/I_DecouplingUserData";
import { Promotion } from "@models/I_Promotion";
import { TRAKERSTYPECODEREID, TrackersCodereId } from "@models/TrackingEvents";
import { DOCUMENT_VERIFICATION_STATES } from "@models/payments/DocumentVerificationStates";
import { DataUserMSOService, GraceTimeStatus, PromoDataAll } from "@mso-services/dataUserMSO.services";
import { UserService } from "@providers/UserService";
import { UserServiceMSO } from "@providers/UserServiceMSO";
import { CommonErrors } from "./registro/registroValidators.service";

enum CookieConsentKey {
  AdPersonalization = "ad_personalization",
  AdStorage = "ad_storage",
  AdUserData = "ad_user_data",
  AnalyticsStorage = "analytics_storage",
  FunctionalityStorage = "functionality_storage",
  PersonalizationStorage = "personalization_storage",
  SecurityStorage = "security_storage",
}

const KEYS_USERDATA_UPDATED = new Map<keyof RequestUpdateData, keyof UserData>([
  ["address", "Direccion"],
  ["job", "Profesion"],
  ["prp", "PersonPrp"],
  ["pepStatement", "DeclaracionPep"],
  ["pepStatementType", "ConfirmPepTipo"],
  ["pepStatementPosition", "PersonPrpCargo"],
  ["pepStatementVinculo", "ConfirmPepVinculo"],
  ["pepStatementTitular", "ConfirmPepTitular"],
  ["familyStatus", "Estado"],
  ["relatedPlayerIdentificadorTributario", "PersoncloselyrelatedwithplayerIdentificadortatributario"],
  ["relatedPlayerLastname", "PersoncloselyrelatedwithplayerLastname"],
  ["relatedPlayerName", "PersoncloselyrelatedwithplayerName"],
  ["relatedPlayerNationality", "PersoncloselyrelatedwithplayerNationality"],
  ["relatedPlayerTipoDocumento", "PersoncloselyrelatedwithplayerTipodocumento"],
  ["relatedPlayerNif", "PersoncloselyrelatedwithplayerNif"],
  ["relatedPlayerTipoIdentificacionTributario", "PersoncloselyrelatedwithplayerTipoidentificaciontributaria"],
]);

enum STATUS_REQUEST {
  OK = "ok",
  KO = "ko",
}

@Injectable({
  providedIn: "root",
})
export class UtilsMsoService {
  private injector = inject(Injector);
  private dataUserServiceMSO = inject(DataUserMSOService);
  private decouplingService = inject(DecouplingUserServiceMSO);
  private userServiceMSO = inject(UserServiceMSO);
  private userService = inject(UserService);

  public static mapUserDataDecoupling(userDataDecoupling: DecouplingUserData): UserData {
    return {
      Nombre: userDataDecoupling.firstName || "",
      PrimerApellido: userDataDecoupling.firstSurName || "",
      SegundoApellido: userDataDecoupling.secondSurName || "",
      Direccion: userDataDecoupling.address || "",
      Direccion_Correo: userDataDecoupling.email || "",
      NIF: userDataDecoupling.documentNumber || "",
      NumMovil: userDataDecoupling.mobilePhone || "",
      CodigoPostal: userDataDecoupling.addressPostalCode || "",
      FechaNacimiento: userDataDecoupling.birthDate || "",
      Profesion: userDataDecoupling.job || "",
      RegistrationDate: userDataDecoupling.registrationDate || "",
      Saldo: userDataDecoupling.balance,
      IdJugador: userDataDecoupling.id,
      OrigenFondos: userDataDecoupling.sourceFunds,
      NombreUsuario: userDataDecoupling.playerUserName,
      VerifiDocumental: userDataDecoupling.documentVerification
        ? DOCUMENT_VERIFICATION_STATES.COMPLETED
        : DOCUMENT_VERIFICATION_STATES.PENDING,
      PersonPrp: userDataDecoupling.prp,
      Municipio: userDataDecoupling.municipality || "",
      Provincia: userDataDecoupling.province || "",
      TipoDocumento: userDataDecoupling.residentType || "",

      AddressDepartment: userDataDecoupling.addressDepartment,
      AddressFloor: userDataDecoupling.addressFloor,
      AddressNumberStreet: userDataDecoupling.addressNumberStreet,

      DeclaracionPep: userDataDecoupling.pepStatement,
      ConfirmPepTipo: userDataDecoupling.pepStatementType,
      ConfirmPepTitular: userDataDecoupling.pepStatementTitular,
      ConfirmPepVinculo: userDataDecoupling.pepStatementVinculo,
      PersonPrpCargo: userDataDecoupling.pepStatementPosition,

      Estado: userDataDecoupling.familyStatus,

      PersoncloselyrelatedwithplayerName: userDataDecoupling.relatedPlayerName || String(null),
      PersoncloselyrelatedwithplayerLastname: userDataDecoupling.relatedPlayerLastname || String(null),
      PersoncloselyrelatedwithplayerNationality: userDataDecoupling.relatedPlayerNationality,
      PersoncloselyrelatedwithplayerTipodocumento: userDataDecoupling.relatedPlayerTipoDocumento,
      PersoncloselyrelatedwithplayerNif: userDataDecoupling.relatedPlayerNif || String(null),
      PersoncloselyrelatedwithplayerTipoidentificaciontributaria:
        userDataDecoupling.relatedPlayerTipoIdentificacionTributario,
      PersoncloselyrelatedwithplayerIdentificadortatributario:
        userDataDecoupling.relatedPlayerIdentificadorTributario,
    };
  }

  public static mapUserUpdatedData(updateFields: RequestUpdateData): Partial<UserData> {
    const keysUpdated = Object.keys(updateFields);
    const result = keysUpdated.map((key) => {
      const keyMap = KEYS_USERDATA_UPDATED.get(key as keyof RequestUpdateData);
      const value = updateFields[key];
      return {
        [keyMap]: value,
      };
    });

    return Object.assign({}, ...result) as Partial<UserData>;
  }

  getUserData(): Observable<UserData> {
    return (
      this.decouplingService.globalVars.FEATURES.MSO_DecouplingUserData
        ? this.decouplingService.getUserData()
        : this.userService.getUserData()
    ).pipe(
      tap((userDataMaped) => {
        this.dataUserServiceMSO.setUserData = userDataMaped;
      }),
    );
  }

  trackCookieConsentStatus(trackers: TrackersCodereId[]) {
    const cookieConsentStatus: CookieConsentStatus = JSON.parse(localStorage.getItem("consentMode") || "null");
    if (cookieConsentStatus) {
      const consentStatusMap = new Map<number, keyof CookieConsentStatus>([
        [TRAKERSTYPECODEREID.CookieConsentStatus_ad_personalization, CookieConsentKey.AdPersonalization],
        [TRAKERSTYPECODEREID.CookieConsentStatus_ad_storage, CookieConsentKey.AdStorage],
        [TRAKERSTYPECODEREID.CookieConsentStatus_ad_user_data, CookieConsentKey.AdUserData],
        [TRAKERSTYPECODEREID.CookieConsentStatus_analytics_storage, CookieConsentKey.AnalyticsStorage],
        [TRAKERSTYPECODEREID.CookieConsentStatus_functionality_storage, CookieConsentKey.FunctionalityStorage],
        [TRAKERSTYPECODEREID.CookieConsentStatus_personalization_storage, CookieConsentKey.PersonalizationStorage],
        [TRAKERSTYPECODEREID.CookieConsentStatus_security_storage, CookieConsentKey.SecurityStorage],
      ]);
      const trackersToCreate: I_DecouplingTrackersCodereID[] = [];
      const trackersToUpdate: I_DecouplingTrackersCodereID[] = [];
      consentStatusMap.forEach((key, value) => {
        const newTracker: I_DecouplingTrackersCodereID = {
          trackerType: value,
          code: cookieConsentStatus[key],
        };

        const existTracker = trackers.find((tracker) => tracker.trackType === value);

        !existTracker
          ? trackersToCreate.push(newTracker)
          : existTracker.value !== newTracker.code && trackersToUpdate.push(newTracker);
      });
      const promises: Promise<TrackersCodereId>[] = [];
      promises.push(
        ...trackersToCreate.map(
          (tracker) => firstValueFrom(this.decouplingService.createTrackersCodereID(tracker)),
          ...trackersToUpdate.map((tracker) =>
            firstValueFrom(this.decouplingService.updateTrackersCodereID(tracker)),
          ),
        ),
      );

      Promise.allSettled(promises)
        .then()
        .catch((_) => {
          console.log("logintrackersError");
        });
    }
  }

  /**
   * @returns User ID encrypted with RSA public key and encodedUriComponent
   */
  getEncrypterUserID() {
    const { id, logged } = this.decouplingService.globalVars.user;
    return id && logged
      ? encodeURIComponent(this.encryptWithPublicKey(`${id};${new Date().toISOString()}`, "MSO_rsa_publickey"))
      : "";
  }

  encryptWithPublicKey(valueToEncrypt: string, feature: keyof C_Features): string {
    const rsa = Forge.pki.publicKeyFromPem(this.formatFeaturePublicKey(feature));
    return window.btoa(rsa.encrypt(valueToEncrypt.toString()));
  }

  private formatFeaturePublicKey(feature: keyof C_Features) {
    const arrayKey = this.decouplingService.globalVars.FEATURES[feature].split(";");
    return arrayKey.join("\n");
  }

  getPromotionsActives(): Observable<{ promoId: string; promoType: string }[]> {
    return toObservable(this.dataUserServiceMSO.getUserPromoData, { injector: this.injector }).pipe(
      map((alldata) => alldata.promotionsActives),
      switchMap((promosActives) => {
        const canUpdate = this.dataUserServiceMSO.canUpdate("promotionsActives")();
        if (promosActives === null || canUpdate) {
          return (
            this.decouplingService.globalVars.FEATURES.MSO_PromotionsDecoupling
              ? this.decouplingService.getPromotionsActives().pipe(map((data) => data.promotions ?? []))
              : this.userServiceMSO.getPromotionsList()
          ).pipe(
            catchError(() => of([])),
            map((response) => {
              if (this.decouplingService.globalVars.FEATURES.MSO_PromotionsDecoupling) {
                return (response as PromotionDecoupling[]).map((promotion) => ({
                  promoId: promotion.promotionExternal,
                  promoType: promotion.product,
                }));
              }
              return response.promotions.map((promotion) => ({
                promoId: promotion.promoId,
                promoType: promotion.promoType,
              }));
            }),
            tap((response) => {
              this.dataUserServiceMSO.setUserPromoData = { promotionsActives: response };
            }),
          );
        } else {
          return of(promosActives);
        }
      }),
    );
  }

  doOptin(promo: Promotion, onlyCheck = false) {
    const { promoProduct, promoId, promoEnd, promoStart, promoType, tyc } = promo;
    const newPromo = {
      category: promoProduct,
      code: promoId,
      endDate: promoEnd,
      startDate: promoStart,
      promoType,
      tyc,
    };
    return (
      this.decouplingService.globalVars.FEATURES.MSO_PromotionsDecoupling
        ? (onlyCheck
            ? this.decouplingService.promotionsCheckOptin(newPromo)
            : this.decouplingService.promotionsOptin(newPromo)
          ).pipe(
            map((response) => {
              if (response.error) {
                return { promotableErrors: response.error.split(",") };
              }

              if (!onlyCheck) {
                return {
                  ...promo,
                  promoStatus: 0,
                };
              }
            }),
          )
        : this.userServiceMSO.setPromotionsOptIn(promo, !onlyCheck)
    ).pipe(
      tap((response) => {
        if (!onlyCheck && (!response.promotableErrors || response.promotableErrors.length === 0)) {
          const newpromosActives = this.dataUserServiceMSO.getUserPromoData().promotionsActives ?? [];
          newpromosActives.push({
            promoId: response.promoId,
            promoType: response.promoType,
          });
          this.dataUserServiceMSO.setUserPromoData = {
            promotionsActives: [...newpromosActives],
          };
        }
      }),
    );
  }

  doOptOut(promo: Promotion) {
    const { promoId } = promo;
    return (
      this.decouplingService.globalVars.FEATURES.MSO_PromotionsDecoupling
        ? this.decouplingService.promotionsOptout(promoId).pipe(
            map((response) => {
              if (response.error) {
                return { promotableErrors: response.error.split(",") };
              }
              return {
                promoId: promo.promoId,
                promoProduct: promo.promoProduct,
                promoStatus: 0,
              };
            }),
          )
        : this.userServiceMSO.setPromotionsOptOut(promo)
    ).pipe(
      tap((response) => {
        if (!response.promotableErrors || response.promotableErrors.length === 0) {
          const newpromosActives = (this.dataUserServiceMSO.getUserPromoData().promotionsActives ?? []).filter(
            (activePromo) => activePromo.promoId !== promoId,
          );
          this.dataUserServiceMSO.setUserPromoData = {
            promotionsActives: [...newpromosActives],
          };
        }
      }),
    );
  }

  getIsPromotable(
    vip = false,
  ): Observable<{ promotableErrors: string[]; isPromotable: boolean; isPromotableVip?: boolean }> {
    return (
      this.decouplingService.globalVars.FEATURES.MSO_PromotionsDecoupling
        ? this.decouplingService.isPromotable(vip).pipe(
            map((response) => {
              if (response.promotableErrors) {
                return {
                  ...response,
                  promotableErrors:
                    response.promotableErrors instanceof Array
                      ? response.promotableErrors
                      : response.promotableErrors.split(","),
                };
              }

              if (response.error) {
                return {
                  ...response,
                  promotableErrors: response.error?.split(","),
                };
              }

              return {
                ...response,
                promotableErrors: [],
              };
            }),
          )
        : vip
          ? this.userServiceMSO.getIsFullPromotionable().pipe(
              map((promoResponse) => {
                (promoResponse.promotableErrors as string[]).push(
                  ...(promoResponse.promotableVipErrors as string[]),
                );
                return {
                  isPromotable: promoResponse.isPromotable,
                  isPromotableVip: promoResponse.isPromotableVip,
                  promotableErrors: promoResponse.promotableErrors,
                };
              }),
            )
          : this.userServiceMSO.getIsPromotionable()
    ).pipe(
      tap((response) => {
        const newDataPromo: Partial<PromoDataAll> = {
          promotableErrors: response.promotableErrors,
        };
        if (vip) {
          newDataPromo.isPromotableVip = response.isPromotableVip;
        } else {
          newDataPromo.isPromotable = response.isPromotable;
        }
        this.dataUserServiceMSO.setUserPromoData = newDataPromo;
      }),
    );
  }

  getLastTermsAndConditions(
    region?: string,
  ): Observable<{ Url: string; Hash: string; Date: string; Code?: string }> {
    return this.decouplingService.globalVars.FEATURES.MSO_TermsAndConditions_Decoupling
      ? this.decouplingService
          .getLastTermsAndConditions()
          .pipe(map(({ url, hash, validityDate }) => ({ Url: url, Hash: hash, Date: validityDate })))
      : this.userServiceMSO.getLastTermsAndConditionsUrlbyRegion(
          region ?? this.decouplingService.globalVars.licenseType.toString(),
        );
  }

  updateTermsAndConditions(userName: string) {
    return this.decouplingService.globalVars.FEATURES.MSO_TermsAndConditions_Decoupling
      ? this.decouplingService
          .updateTermsAndConditions(userName)
          .pipe(map(() => ({ status: STATUS_REQUEST.OK.toString() })))
      : this.userServiceMSO.updateTermsAndConditions(userName).pipe(map(({ status }) => ({ status })));
  }

  getLastTermsAndConditionsSelfie(
    region?: string,
  ): Observable<{ Url: string; Hash: string; Date: string; Code?: string }> {
    return this.decouplingService.globalVars.FEATURES.MSO_TermsAndConditionsSelfie_Decoupling
      ? this.decouplingService
          .getLastTermsAndConditionsSelfie()
          .pipe(map(({ url, hash, validityDate }) => ({ Url: url, Hash: hash, Date: validityDate })))
      : this.userServiceMSO.getLastTermsAndConditionsSelfieUrlbyRegion(
          region ?? this.decouplingService.globalVars.licenseType.toString(),
        );
  }

  updateTermsAndConditionsSelfie() {
    return this.decouplingService.globalVars.FEATURES.MSO_TermsAndConditionsSelfie_Decoupling
      ? this.decouplingService.updateLastTermsAndConditionsSelfie(this.decouplingService.globalVars.user.username)
      : this.userServiceMSO.updateTyCSelfie({});
  }

  getLastPrivacyPolitics(region?: string): Observable<{ Url: string; Hash: string; Date: string; Code?: string }> {
    return this.decouplingService.globalVars.FEATURES.MSO_PrivacyPolicy_Decoupling
      ? this.decouplingService
          .getLastPrivacyPolitics()
          .pipe(map(({ url, hash, validityDate }) => ({ Url: url, Hash: hash, Date: validityDate })))
      : this.userServiceMSO.getLastPrivacyPoliticsUrlbyRegion(
          region ?? this.decouplingService.globalVars.licenseType.toString(),
        );
  }

  updatePrivacePolitics(userName: string) {
    return this.decouplingService.globalVars.FEATURES.MSO_PrivacyPolicy_Decoupling
      ? this.decouplingService
          .updatePrivacyPolitics(userName)
          .pipe(map(() => ({ status: STATUS_REQUEST.OK.toString() })))
      : this.userServiceMSO.updatePrivacePolitics(userName).pipe(map(({ status }) => ({ status })));
  }

  getLastAuthorizationPersonalData(
    region?: string,
  ): Observable<{ Url: string; Hash: string; Date: string; Code?: string }> {
    return this.decouplingService.globalVars.FEATURES.MSO_AuthorizationPersonalData_Decoupling
      ? this.decouplingService
          .getAuthorizationPersonalData()
          .pipe(map(({ url, hash, validityDate }) => ({ Url: url, Hash: hash, Date: validityDate })))
      : this.userServiceMSO.getLastAutoTrataDatosPersonalUrl(
          region ?? this.decouplingService.globalVars.licenseType.toString(),
        );
  }

  updateAuthorizationPersonalData(userName: string) {
    return this.decouplingService.globalVars.FEATURES.MSO_AuthorizationPersonalData_Decoupling
      ? this.decouplingService
          .updateAuthorizationPersonalData(userName)
          .pipe(map(() => ({ status: STATUS_REQUEST.OK.toString() })))
      : this.userServiceMSO.updateAuthorizationPersonalData(userName).pipe(map(({ status }) => ({ status })));
  }

  checkDataAvailability(table: DataTables, value: string): Observable<ValidationErrors | null> {
    return this.decouplingService.globalVars.FEATURES.MSO_NewDecouplingServiceSignup
      ? this.decouplingService.checkDataAvailability(table, value).pipe(
          map(({ exists }) => (exists ? { validUnique: CommonErrors.NotValid } : null)),
          catchError(() => of(null)),
        )
      : this.userServiceMSO.checkDataAvailability(table, value);
  }

  verifyVoucher(code: string) {
    return this.decouplingService.globalVars.FEATURES.MSO_Check_PromoCode_Decoupling
      ? this.decouplingService
          .checkDataAvailability(DataTables.PROMO_CODE, code)
          .pipe(map((response) => (response.exists ? null : { validFormat: STATUS_REQUEST.KO })))
      : this.userServiceMSO.verifyCode(code).pipe(
          map((response) => {
            if (response) {
              if (response.status) {
                if (response.status.toLowerCase() !== STATUS_REQUEST.KO) {
                  return null;
                } else {
                  return { validFormat: response.errCode };
                }
              } else {
                return null;
              }
            } else {
              return null;
            }
          }),
        );
  }

  setGTMTrackers(): Promise<TrackersCodereId[]> {
    return new Promise((resolve, reject) => {
      const observer = this.decouplingService.globalVars.FEATURES.MSO_DecouplingTrackersCodereId
        ? this.decouplingService.getTrackersCodereID()
        : this.userService.getTrackersCurrentUser();
      observer.subscribe({
        next: (trackers: TrackersCodereId[]) => {
          const GTMTrackers = {
            Mexos_Advertiser: "",
            Mexos_Aquisition_Source: "",
            Mexos_ID2: "",
            Mexos_ID3: "",
            Mexos_ProfileID: "",
          };
          trackers.forEach((track) => {
            switch (true) {
              case track.trackType === TRAKERSTYPECODEREID.mexosAquisitionSource:
                GTMTrackers.Mexos_Aquisition_Source = track.value;
                break;
              case track.trackType === TRAKERSTYPECODEREID.mexosAdvertiser:
                GTMTrackers.Mexos_Advertiser = track.value;
                break;
              case track.trackType === TRAKERSTYPECODEREID.mexosID2:
                GTMTrackers.Mexos_ID2 = track.value;
                break;
              case track.trackType === TRAKERSTYPECODEREID.mexosID3:
                GTMTrackers.Mexos_ID3 = track.value;
                break;
              case track.trackType === TRAKERSTYPECODEREID.mexosProfileID:
                GTMTrackers.Mexos_ProfileID = track.value;
                break;
            }
          });
          this.decouplingService.globalVars.user.codereIdTracker = GTMTrackers;
          resolve(trackers);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }
  getDateDifference(startDate: Date, endDate: Date) {
    const dateStartYear = 1970;
    const dayStart = 1;
    if (startDate > endDate) {
      return null;
    }

    const diffInMilliseconds = endDate.getTime() - startDate.getTime();
    const diffDate = new Date(diffInMilliseconds);

    const years = diffDate.getUTCFullYear() - dateStartYear;
    const months = diffDate.getUTCMonth();
    const days = diffDate.getUTCDate() - dayStart;

    return { years, months, days };
  }

  setGracetime(
    verification: boolean,
    filter?: {
      value: GraceTimeStatus;
      dateToChek?: Date | number | string;
      maxDate?: Date | number | string;
      maxTime: number;
    },
  ) {
    const defaultValue = {
      graceTime: GraceTimeStatus.ON_TIME,
      documentationVerification: verification,
    };
    if (!verification && filter && filter.dateToChek) {
      try {
        const timenow = new Date();
        const graceTime = new Date(filter.dateToChek);
        graceTime.setDate(graceTime.getDate() + filter.maxTime);

        const auxGraceTime =
          filter.maxDate &&
          (new Date(filter.maxDate).getTime() < graceTime.getTime() ||
            new Date(filter.maxDate).getTime() < timenow.getTime())
            ? new Date(filter.maxDate)
            : graceTime;

        this.dataUserServiceMSO.setExtraUserData = {
          graceTime:
            timenow.getTime() <= auxGraceTime.getTime()
              ? GraceTimeStatus.ON_TIME
              : !filter.maxDate
                ? filter.value
                : graceTime.getTime() === auxGraceTime.getTime()
                  ? filter.value
                  : GraceTimeStatus.OUT_OF_TIME_REGISTER,
          documentationVerification: verification,
        };
      } catch (_) {
        this.dataUserServiceMSO.setExtraUserData = defaultValue;
      }
    } else {
      this.dataUserServiceMSO.setExtraUserData = defaultValue;
    }
  }
}
